// Every device a person uses the portal on (William, 25 Sep 2026).
//
// A browser has no stable id of its own, so this one gets a random id the
// first time the portal runs in it, kept in localStorage. Once a day per
// person it tells the database "this person, on this device"
// (record_user_device, migration 20260925070000). Clearing the browser's
// data makes it a new device - the most anyone can know without tracking
// the hardware, which a website cannot and should not do.
import { supabase } from "@/integrations/supabase/client";
import { isTeslaBrowser, TESLA_MARK } from "@/lib/tesla";

const ID_KEY = "cha-device-id";
const seenKey = (userId: string) => `cha-device-seen:${userId}`;
const DAY_MS = 24 * 60 * 60 * 1000;

function deviceId(): string | null {
  try {
    let id = localStorage.getItem(ID_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(ID_KEY, id);
    }
    return id;
  } catch {
    // No storage (a private window): nothing stable to name the device by.
    return null;
  }
}

/** The user agent as recorded: the car's browser marked (lib/tesla.ts). */
export function recordedUserAgent(): string {
  return navigator.userAgent + (isTeslaBrowser() ? ` ${TESLA_MARK}` : "");
}

/** At most once a day per person per browser. Never throws. */
export async function recordDevice(userId: string): Promise<void> {
  try {
    const id = deviceId();
    if (!id) return;
    const last = Number(localStorage.getItem(seenKey(userId)) ?? 0);
    if (Date.now() - last < DAY_MS) return;
    const installed = window.matchMedia("(display-mode: standalone)").matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const { error } = await supabase.rpc("record_user_device" as never, {
      p_device_id: id,
      p_user_agent: recordedUserAgent(),
      p_is_installed: installed,
    } as never);
    // Before the migration is applied the function does not exist; try again
    // tomorrow rather than on every page.
    if (!error || (error as { code?: string }).code === "PGRST202") {
      localStorage.setItem(seenKey(userId), String(Date.now()));
    }
  } catch {
    /* recording a device must never get in anyone's way */
  }
}

/** What a stored user agent says: the device kind and the browser. */
export function describeUserAgent(ua: string | null): { device: string; browser: string } {
  if (!ua) return { device: "Unknown", browser: "Unknown" };
  if (ua.includes(TESLA_MARK)) return { device: "Tesla", browser: "In-car browser" };
  let device = "Desktop";
  if (ua.includes("iPad")) device = "iPad";
  else if (ua.includes("iPhone")) device = "iPhone";
  else if (ua.includes("Android")) device = ua.includes("Mobile") ? "Android phone" : "Android tablet";
  else if (ua.includes("Macintosh")) device = "Mac";
  else if (ua.includes("Windows")) device = "Windows";
  else if (ua.includes("Mobile")) device = "Phone";
  let browser = "Unknown";
  if (ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("OPR") || ua.includes("Opera")) browser = "Opera";
  else if (ua.includes("CriOS") || (ua.includes("Chrome") && !ua.includes("Edg"))) browser = "Chrome";
  else if (ua.includes("FxiOS") || ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";
  return { device, browser };
}
